import React, {useEffect, useRef, useState} from 'react'
import {Button} from '../../../../../core/shared/Button'
import {currencyFormat} from '../../../../../utils/currency-format'
import {getPriceForCountry} from '../../../utils/getPriceForCountry'
import {PlanDynamicField} from '../PlanDynamicField'
import {ProposalIdField} from '../fields/ProposalIdField'
import {QuantityStepper} from '../../QuantityStepper'
import {
  getFieldsFromPlan,
  getDefaultValues,
  validatePlanFields,
} from '../planFieldsConfig'
import {
  FieldsGrid,
  AddToOrderRow,
  AddonStepperList,
  AddonStepperRow,
  AddonStepperInfo,
  AddonStepperName,
  AddonStepperPrice,
  PanelContainer,
} from '../ProductPlanSelector.styles'

const EXTERNAL_DOMAIN_NOTE =
  'Please Note: External site requires the client to provide website content management system & hosting access at onboarding. SEO order is subject to cancellation if access is not provided at onboarding to avoid billing client for inactive SEO campaign.'

const BILLING_DATE_DISABLED_NOTE =
  'Thryv Website must be included on the order to enable a different billing start date.'

// Products that count as "Thryv Website" for gating billingDifferentDate.
const WEBSITE_PLAN_CODES = ['addon-5-website_difm', 'ent-5-website']

// The catalog API doesn't return a maxQty for the SEO Keywords addon —
// hardcode its cap here until the API provides one.
const KEYWORDS_ADDON_CODE = 'addon-5-seokey-m2m'
const KEYWORDS_ADDON_MAX_QTY = 5

// FULSAS-9187: SEO PowerBoost is capped at 3. The catalog API doesn't return a maxQty for it
// either, so hardcode the cap here (mirrors the backend order validation).
const POWERBOOST_ADDON_CODE = 'addon-5-seopb-m2m'
const POWERBOOST_ADDON_MAX_QTY = 3

// Add-ons offered as quantity steppers under the AEO & SEO plan. Selected
// quantities are attached to the plan's own `addons` array as nested order
// line items (see handleAddToOrder below).
//
// The catalog API doesn't nest these under their parent plan — they come
// back as their own top-level products (in productConfigInfo.apps) that
// point at the parent via `requiresAddon`, so look them up that way instead
// of a hardcoded list.
export function getAddonsForPlan(planCode, productConfigInfo) {
  const {centers = [], apps = [], oneTimeFees = []} = productConfigInfo ?? {}
  return [...centers, ...apps, ...oneTimeFees]
    .filter(product => product.requiresAddon?.includes(planCode))
    .map(product => {
      if (product.code === KEYWORDS_ADDON_CODE)
        return {...product, maxQty: KEYWORDS_ADDON_MAX_QTY}
      if (product.code === POWERBOOST_ADDON_CODE)
        return {...product, maxQty: POWERBOOST_ADDON_MAX_QTY}
      return product
    })
}

// Thryv Website can be in the order as its own selected product, as an
// add-on nested under another product (e.g. bundled with a center), or as
// an entitlement granted by another product — check all three.
function includesWebsiteCode(codes) {
  return codes?.some(code => WEBSITE_PLAN_CODES.includes(code))
}

function productHasThryvWebsite(product) {
  if (WEBSITE_PLAN_CODES.includes(product.planCode)) return true
  if (includesWebsiteCode(product.addons?.map(a => a.productCode ?? a.code))) {
    return true
  }
  return includesWebsiteCode(
    product.entitlements?.map(e => e.code ?? e.productCode),
  )
}

const DEFAULT_PRICE = 700

// getPriceForCountry returns 0 when the plan/addon has no currency entry for
// the selected country — fall back to a default rather than pricing at $0.
function priceForCountry(currencies, country) {
  return getPriceForCountry(currencies, country) || DEFAULT_PRICE
}

// Fields come back from the DB in whatever order the catalog API returns
// them, not necessarily the order this panel wants to display them in — so
// re-sort by key here. Unknown keys (future DB additions) sort to the end.
const FIELD_ORDER = [
  'proposalId',
  'externalDomain',
  'domain',
  'proposalAmount',
  'billingDifferentDate',
  'billingDate',
]

function sortFieldsForDisplay(fields) {
  return [...fields].sort((a, b) => {
    const orderA = FIELD_ORDER.indexOf(a.key)
    const orderB = FIELD_ORDER.indexOf(b.key)
    return (
      (orderA === -1 ? FIELD_ORDER.length : orderA) -
      (orderB === -1 ? FIELD_ORDER.length : orderB)
    )
  })
}

// Account-owned items (alreadyIncluded, from an upgrade) carry their
// configuration as the Recurly custom-fields shape — an array of
// {name, value} pairs — rather than the {fieldKey: value} object this panel
// writes on submit. Map the ones we recognize so reopening still pre-fills.
const ACCOUNT_CONFIG_NAME_TO_KEY = {
  SEO_Proposal_ID: 'proposalId',
  Domain: 'domain',
}

function normalizeExistingConfiguration(configuration) {
  if (!configuration) return {}
  if (!Array.isArray(configuration)) {
    // Older subscription data may surface this as isExternalDomain rather
    // than the panel's yes/no radio value. Normalize it on re-entry so both
    // persisted cart items and account-owned subscriptions render correctly.
    if (
      configuration.externalDomain == null &&
      configuration.isExternalDomain != null
    ) {
      return {
        ...configuration,
        externalDomain:
          configuration.isExternalDomain === true ||
          String(configuration.isExternalDomain).toLowerCase() === 'yes'
            ? 'yes'
            : 'no',
      }
    }
    return configuration
  }
  return configuration.reduce((acc, {name, value}) => {
    const key = ACCOUNT_CONFIG_NAME_TO_KEY[name]
    if (key) acc[key] = value
    return acc
  }, {})
}

// The order only needs the proposal itself (id/amount/domain) plus the
// billing start date when the customer picked one — externalDomain,
// billingDifferentDate, and proposalValidated are just this panel's own
// UI state and shouldn't be sent along.
function buildOrderConfiguration(values) {
  const config = {
    proposalId: values.proposalId,
    proposalAmount: values.proposalAmount,
    domain: values.domain,
    externalDomain: values.externalDomain,
  }
  if (values.billingDate) {
    config.billingDate = values.billingDate
  }
  return config
}

// Hide/show + notes can't live in planFieldsConfig.js (that config mirrors a
// plain DB response — no functions or nested objects), so this bespoke panel
// keys that behavior off field.key locally instead.
//
// billingDifferentDate/billingDate never show for an already-included
// (account-owned, upgrade) item — its billing is already active, so
// picking a future start date doesn't apply.
function isFieldVisible(field, values, isAlreadyIncluded) {
  if (field.key === 'billingDifferentDate') {
    return (
      !isAlreadyIncluded &&
      values.externalDomain === 'no' &&
      values.proposalValidated === true
    )
  }
  if (field.key === 'billingDate') {
    return (
      !isAlreadyIncluded &&
      values.externalDomain === 'no' &&
      values.proposalValidated === true &&
      values.billingDifferentDate === 'yes'
    )
  }
  return true
}

function decorateField(field) {
  if (field.key === 'externalDomain') {
    return {...field, notes: {yes: EXTERNAL_DOMAIN_NOTE}}
  }
  // domain and proposalAmount are always auto-filled from the proposal ID
  // validate response — never manually editable.
  if (field.key === 'proposalAmount' || field.key === 'domain') {
    return {...field, readOnly: true}
  }
  return field
}

// Owns everything specific to the AEO & SEO plan: the DB-provided fields
// (merged with local hide/show + notes), the proposal ID validate/autofill
// flow, and the PowerBoost/Keywords add-on steppers. Renders inside
// ProductPlanSelector's right panel in place of the generic fields+addons UI.
//
// itemInOrder, when the plan is already in the order, carries the item's
// prior configuredOptions/addons so this reopens pre-filled (see "View
// Configuration" re-entry in AddonsGrid) — addons flagged alreadyIncluded
// (account-owned, from an upgrade) can only be incremented, never removed
// below their existing quantity.
export function SeoAddonPanel({
  plan,
  selectedCountry,
  selectedProducts,
  itemInOrder,
  groupPlanCodes,
  groupId,
  productConfigInfo,
  onAddProduct,
  onUpdateProduct,
  onClose,
}) {
  const fields = sortFieldsForDisplay(getFieldsFromPlan(plan))
  const addons = getAddonsForPlan(plan.code, productConfigInfo)
  const isItemInOrder = Boolean(itemInOrder)
  const isItemAlreadyIncluded = Boolean(itemInOrder?.alreadyIncluded)
  const hasThryvWebsite = selectedProducts?.some(productHasThryvWebsite)
  const proposalIdRef = useRef(null)

  const [fieldValues, setFieldValues] = useState(() => ({
    ...getDefaultValues(fields),
    // An already-added item was validated when it was first added.
    proposalValidated: isItemInOrder,
    ...normalizeExistingConfiguration(itemInOrder?.configuredOptions),
  }))
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [addonQuantities, setAddonQuantities] = useState(() => {
    const initial = {}
    addons.forEach(addon => {
      const addonInOrder = itemInOrder?.addons?.find(
        a => a.productCode === addon.code,
      )
      initial[addon.code] = addonInOrder?.quantity ?? 0
    })
    return initial
  })

  const proposalIdField = fields.find(f => f.key === 'proposalId')
  const otherFields = fields.filter(
    f =>
      f.key !== 'proposalId' &&
      isFieldVisible(f, fieldValues, isItemAlreadyIncluded),
  )

  // Account data (upgrade scenario) only carries the proposal ID and domain
  // — not the proposal amount. Re-run the validate call once on mount to
  // fetch and fill in proposalAmount when it's missing.
  useEffect(() => {
    if (
      isItemInOrder &&
      fieldValues.proposalId &&
      !fieldValues.proposalAmount
    ) {
      proposalIdRef.current?.validate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({
      ...prev,
      [key]: value,
      // Editing the proposal ID invalidates the prior validation, hiding
      // billingDifferentDate/billingDate again until it's re-validated.
      ...(key === 'proposalId' && {proposalValidated: false}),
    }))
    setErrors(prev => ({...prev, [key]: null}))
  }

  const handleFieldBlur = key => {
    setTouched(prev => ({...prev, [key]: true}))
    const fieldErrors = validatePlanFields(otherFields, fieldValues)
    setErrors(prev => ({...prev, [key]: fieldErrors[key] ?? null}))
  }

  const handleProposalValidated = (isValid, res) => {
    setFieldValues(prev => ({
      ...prev,
      proposalValidated: isValid,
      ...(isValid &&
        res?.proposalInfo && {
          domain: res.proposalInfo.domain,
          proposalAmount: res.proposalInfo.price,
        }),
    }))
  }

  const addonFloor = code => {
    const addonInOrder = itemInOrder?.addons?.find(a => a.productCode === code)
    return addonInOrder?.alreadyIncluded ? addonInOrder.quantity ?? 0 : 0
  }

  const handleAddonQuantityChange = (code, quantity) => {
    setAddonQuantities(prev => ({...prev, [code]: quantity}))
  }

  const handleAddToOrder = async () => {
    const proposalIdValue = fieldValues.proposalId ?? ''
    if (!String(proposalIdValue).trim()) {
      setTouched(prev => ({...prev, proposalId: true}))
      setErrors(prev => ({
        ...prev,
        proposalId: 'Enter Proposal ID is required',
      }))
      return
    }

    if (!isItemInOrder) {
      const isValid = await proposalIdRef.current?.validate()
      if (!isValid) {
        setTouched(prev => ({...prev, proposalId: true}))
        setErrors(prev => ({
          ...prev,
          proposalId: 'Please enter a valid Proposal ID',
        }))
        return
      }
    }

    const fieldErrors = validatePlanFields(otherFields, fieldValues)
    const allTouched = otherFields.reduce(
      (acc, f) => ({...acc, [f.key]: true}),
      {proposalId: true},
    )
    setTouched(prev => ({...prev, ...allTouched}))
    setErrors(prev => ({...prev, ...fieldErrors}))
    if (Object.keys(fieldErrors).length > 0) return

    const billingFrequency =
      plan.productBillingType === 'Annual' ? 'annual' : 'monthly'

    const orderAddons = addons
      .map(addon => {
        const quantity = addonQuantities[addon.code] ?? 0
        if (quantity <= 0) return null
        const addonInOrder = itemInOrder?.addons?.find(
          a => a.productCode === addon.code,
        )
        // Bumping the quantity above the account's original count means
        // it's no longer purely account-owned — clear the flag so the
        // increment gets billed instead of silently staying "included".
        const quantityChanged = quantity !== (addonInOrder?.quantity ?? 0)
        return {
          code: addon.code,
          name: addon.name,
          productType: 'addon',
          unitPrice: priceForCountry(addon.currencies, selectedCountry),
          quantity,
          billingFrequency,
          alreadyIncluded: Boolean(
            addonInOrder?.alreadyIncluded && !quantityChanged,
          ),
        }
      })
      .filter(Boolean)

    const product = {
      productId: plan.code,
      productCode: plan.code,
      name: plan.name,
      quantity: plan.maxQty,
      price: plan.maxQty * fieldValues['proposalAmount'],
      productType: 'APP',
      billingFrequency,
      configuredOptions: buildOrderConfiguration(fieldValues),
      addons: orderAddons,
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      parentProduct: plan.parentProduct,
    }

    if (isItemInOrder) {
      // Falls back to onAddProduct if the host screen hasn't wired up
      // onUpdateProduct — best-effort rather than a hard crash.
      ;(onUpdateProduct ?? (p => onAddProduct(p, groupPlanCodes, groupId)))(
        product,
      )
    } else {
      onAddProduct(product, groupPlanCodes, groupId)
    }
    onClose()
  }

  return (
    <PanelContainer>
      <FieldsGrid>
        {proposalIdField && (
          <ProposalIdField
            ref={proposalIdRef}
            field={proposalIdField}
            value={fieldValues.proposalId ?? ''}
            variant="form"
            size="md"
            error={touched.proposalId ? errors.proposalId : null}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            onValidated={handleProposalValidated}
          />
        )}
        {otherFields.map(field => (
          <PlanDynamicField
            key={field.key}
            field={decorateField(field)}
            value={fieldValues[field.key] ?? ''}
            values={fieldValues}
            error={touched[field.key] ? errors[field.key] : null}
            touched={touched[field.key]}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            variant="form"
            size="md"
            disabled={
              field.key === 'billingDifferentDate'
                ? !hasThryvWebsite
                : undefined
            }
            disabledNote={
              field.key === 'billingDifferentDate'
                ? BILLING_DATE_DISABLED_NOTE
                : undefined
            }
          />
        ))}
      </FieldsGrid>

      {addons.length > 0 && (
        <AddonStepperList>
          {addons.map(addon => {
            const quantity = addonQuantities[addon.code] ?? 0
            const price = priceForCountry(addon.currencies, selectedCountry)
            const min = addonFloor(addon.code)
            return (
              <AddonStepperRow key={addon.code}>
                <AddonStepperInfo>
                  <AddonStepperName>{addon.name}</AddonStepperName>
                  <AddonStepperPrice>
                    {currencyFormat(price, selectedCountry)}/mo
                  </AddonStepperPrice>
                </AddonStepperInfo>
                <QuantityStepper
                  quantity={quantity}
                  min={min}
                  maxQuantity={addon.maxQty ?? null}
                  label={`${addon.name} quantity`}
                  onIncrement={() =>
                    handleAddonQuantityChange(addon.code, quantity + 1)
                  }
                  onDecrement={() =>
                    handleAddonQuantityChange(
                      addon.code,
                      Math.max(min, quantity - 1),
                    )
                  }
                  onQuantityInput={next =>
                    handleAddonQuantityChange(addon.code, next)
                  }
                />
              </AddonStepperRow>
            )
          })}
        </AddonStepperList>
      )}

      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          onClick={handleAddToOrder}
        >
          {/* {isItemInOrder ? 'Update Order' : 'Add to Order'} */}
          {'Add to Order'}
        </Button>
      </AddToOrderRow>
    </PanelContainer>
  )
}
